/* DateSelect — modern Month / Day / Year picker (iter102).

   Replaces the native <input type="date"> whose desktop popup forces
   users to click backwards month-by-month to reach decades-old birth
   dates. Three clearly-labelled dropdowns let a visitor jump straight to
   any year. Feels like the birth-date selector on a signup form.

   - Value in/out is the canonical "YYYY-MM-DD" string (unchanged backend
     format). Emits "" when incomplete, so the existing optional-date
     handling keeps working.
   - `max` / `min` ("YYYY-MM-DD") clamp selectable years (e.g. death date
     cannot be in the future; birth cannot exceed death).
   - Day options recompute for the chosen month + year (leap years too). */
import React, { useEffect, useMemo, useState } from 'react';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function parse(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!m) return { y: '', mo: '', d: '' };
  return { y: m[1], mo: m[2], d: m[3] };
}

function daysInMonth(year, month) {
  if (!month) return 31;
  // Unknown year: assume leap year so February offers 29 days.
  return new Date(year ? Number(year) : 2000, Number(month), 0).getDate();
}

const selectCls =
  'ml-input !py-2.5 appearance-none bg-[hsl(var(--surface,0_0%_100%))] cursor-pointer';

export function DateSelect({
  value,
  onChange,
  max,
  min,
  minYear = 1900,
  testIdPrefix = 'date',
}) {
  // Internal state keeps partial picks (e.g. just a month) alive even
  // though the parent value round-trips as "" until the date is complete.
  const [sel, setSel] = useState(() => parse(value));
  const { y, mo, d } = sel;
  const now = new Date();

  useEffect(() => {
    const p = parse(value);
    if (p.y) {
      if (p.y !== y || p.mo !== mo || p.d !== d) setSel(p);
    } else if (y && mo && d) {
      setSel({ y: '', mo: '', d: '' }); // parent cleared a complete date
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const maxY = max ? Number(parse(max).y) || now.getFullYear() : now.getFullYear();
  const minY = min ? Number(parse(min).y) || minYear : minYear;

  const years = useMemo(() => {
    const top = maxY;
    const bottom = Math.min(minY, top);
    const arr = [];
    for (let yr = top; yr >= bottom; yr--) arr.push(yr);
    return arr;
  }, [maxY, minY]); // eslint-disable-line react-hooks/exhaustive-deps

  const dayCount = daysInMonth(y, mo);
  const days = useMemo(
    () => Array.from({ length: dayCount }, (_, i) => i + 1),
    [dayCount],
  );

  const emit = (ny, nmo, nd) => {
    let day = nd;
    if (nd && nmo) {
      const cap = daysInMonth(ny, nmo);
      if (Number(nd) > cap) day = String(cap); // clamp Feb 30 → Feb 28/29 etc.
    }
    setSel({ y: ny, mo: nmo, d: day });
    if (ny && nmo && day) {
      onChange(`${ny}-${String(nmo).padStart(2, '0')}-${String(Number(day)).padStart(2, '0')}`);
    } else {
      onChange('');
    }
  };

  // If the linked date changes (e.g. death year set below a chosen birth
  // year), pull the out-of-range year back inside the allowed window.
  useEffect(() => {
    if (y && Number(y) > maxY) emit(String(maxY), mo, d);
    else if (y && Number(y) < minY) emit(String(minY), mo, d);
  }, [maxY, minY]); // eslint-disable-line react-hooks/exhaustive-deps

  const labelCls = 'block text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.55)] mb-1 font-semibold';

  return (
    <div className="grid grid-cols-3 gap-2" data-testid={`${testIdPrefix}-select`}>
      <div>
        <label className={labelCls} htmlFor={`${testIdPrefix}-month`}>Month</label>
        <select
          id={`${testIdPrefix}-month`}
          className={selectCls}
          value={mo}
          onChange={(e) => emit(y, e.target.value, d)}
          data-testid={`${testIdPrefix}-month`}
        >
          <option value="">Month</option>
          {MONTHS.map((name, i) => (
            <option key={name} value={String(i + 1).padStart(2, '0')}>{name}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelCls} htmlFor={`${testIdPrefix}-day`}>Day</label>
        <select
          id={`${testIdPrefix}-day`}
          className={selectCls}
          value={d ? String(Number(d)) : ''}
          onChange={(e) => emit(y, mo, e.target.value)}
          data-testid={`${testIdPrefix}-day`}
        >
          <option value="">Day</option>
          {days.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelCls} htmlFor={`${testIdPrefix}-year`}>Year</label>
        <select
          id={`${testIdPrefix}-year`}
          className={selectCls}
          value={y}
          onChange={(e) => emit(e.target.value, mo, d)}
          data-testid={`${testIdPrefix}-year`}
        >
          <option value="">Year</option>
          {years.map((yr) => (
            <option key={yr} value={yr}>{yr}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

export default DateSelect;
